import { Navigate, Outlet } from "@tanstack/react-router";
import { FolderPlusIcon } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useWorkspaces } from "../../api/registry-queries";
import { useRuntimeConfig } from "../../app/config-context";
import { RouteLink } from "../../app/route-link";
import { Button } from "../../ui/button";
import { CliCommand } from "../../ui/cli-command";
import { EmptyState } from "../../ui/empty-state";
import { ErrorState } from "../../ui/error-state";
import { Skeleton } from "../../ui/skeleton";
import { usePreferences, useUpdatePreferences } from "../account";
import { routes } from "./paths";
import { useCosmosContext } from "./use-context";

/** The URL of the cosmos API as the CLI reaches it: the configured one, else this origin. */
export function useServerUrl(): string {
  const { cosmosApiUrl } = useRuntimeConfig();
  return cosmosApiUrl || window.location.origin;
}

/** `/`: the last workspace, else the first one, else how to create one. */
export function WorkspaceRedirect() {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  const preferences = usePreferences();
  const server = useServerUrl();
  if (workspaces.isError) {
    return (
      <ErrorState
        error={workspaces.error}
        title={t("context.unreachable")}
        action={
          <Button variant="outline" size="sm" asChild>
            <RouteLink to={routes.health()}>{t("health.nav")}</RouteLink>
          </Button>
        }
      />
    );
  }
  if (workspaces.isPending || preferences.isPending) {
    return <Skeleton className="h-24 w-full" aria-label={t("context.loading")} />;
  }
  const names = workspaces.data.map((w) => w.name);
  const last = preferences.data?.lastWorkspace;
  const target = last && names.includes(last) ? last : names[0];
  if (!target) {
    return (
      <EmptyState
        icon={FolderPlusIcon}
        title={t("context.noWorkspace")}
        text={t("context.noWorkspaceText")}
        action={
          <CliCommand
            className="mx-auto max-w-xl text-left"
            command={`cosmos workspace create <name> --server ${server}`}
          />
        }
      />
    );
  }
  const home = routes.home(target);
  return <Navigate to={home.to} params={home.params as never} replace />;
}

/** Every /w/$workspace page: the workspace is remembered for the next visit. */
export function WorkspaceLayout() {
  const { workspace } = useCosmosContext();
  const { data } = usePreferences();
  const { mutate } = useUpdatePreferences();
  const saved = data?.lastWorkspace;
  // Saved once the preferences are read, so an unreachable web API never raises an error here.
  useEffect(() => {
    if (data && workspace && saved !== workspace) mutate({ lastWorkspace: workspace });
  }, [data, saved, workspace, mutate]);
  return <Outlet />;
}
